import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ConversationList from './components/ConversationList';
import MessageThread from './components/MessageThread';
import ConversationStats from './components/ConversationStats';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import twilioService from '../../lib/twilio-service';

function messageSignature(messages) {
  return (messages || [])
    .map((m) => `${m.id}:${m.status || ''}:${m.content || ''}`)
    .join('|');
}

function formatConversation(conv) {
  const latest = Array.isArray(conv.latest_message) ? conv.latest_message[0] : null;
  return {
    id: conv.id,
    client: {
      name: conv.client?.name,
      phone: conv.client_phone,
      avatar: conv.client?.avatar_url,
      avatarAlt: `${conv.client?.name || 'Client'} avatar`
    },
    channel: conv.channel,
    unreadCount: conv.unread_count || 0,
    responseRequired: (conv.unread_count || 0) > 0,
    lastMessage: latest ? {
      content: latest.content,
      timestamp: new Date(latest.created_at),
      isFromAgent: latest.direction === 'outbound',
      status: latest.status
    } : null,
    messages: [],
    clientId: conv.client_id,
    twilioPhone: conv.twilio_phone
  };
}

const Conversations = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileThreadView, setIsMobileThreadView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const selectedIdRef = useRef(null);
  const sessionRef = useRef(0);
  const loadTokenRef = useRef(0);
  const openedFromNavRef = useRef(location.state?.conversationId || null);
  const appliedNavIdRef = useRef(null);
  selectedIdRef.current = selectedConversation?.id ?? null;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user?.id) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Load messages for a conversation. Background refreshes must not replace the
  // open thread or the reply draft.
  const loadConversationMessages = async (conversation, { background = false, session = sessionRef.current } = {}) => {
    if (!conversation?.id) return;
    const token = background ? loadTokenRef.current : ++loadTokenRef.current;
    const startedAt = Date.now();

    if (!background) {
      setSelectedConversation((prev) => ({
        ...conversation,
        messages: prev?.id === conversation.id ? (prev.messages || []) : (conversation.messages || [])
      }));
    }

    try {
      const { data, error: msgError } = await twilioService.getMessages(conversation.id);
      if (sessionRef.current !== session) return;
      if (!background && loadTokenRef.current !== token) return;
      if (background && selectedIdRef.current !== conversation.id) return;

      if (msgError) {
        console.error('Error loading messages:', msgError);
        if (!background) {
          setSelectedConversation((prev) => (
            prev?.id === conversation.id ? prev : { ...conversation, messages: [] }
          ));
        }
        return;
      }

      const formattedMessages = (data || []).map(msg => ({
        id: msg.id,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        isFromAgent: msg.direction === 'outbound',
        type: msg.direction === 'outbound' ? 'agent' : 'client',
        status: msg.status,
        twilioSid: msg.twilio_sid
      }));

      setSelectedConversation((prev) => {
        if (selectedIdRef.current && selectedIdRef.current !== conversation.id) return prev;
        const base = prev?.id === conversation.id ? prev : conversation;
        const serverIds = new Set(formattedMessages.map((m) => m.id));
        const extras = (base.messages || []).filter((m) => {
          if (serverIds.has(m.id)) return false;
          const sentAt = new Date(m.timestamp).getTime();
          return String(m.id).startsWith('temp-') || sentAt >= startedAt - 5000;
        });
        const merged = [...formattedMessages, ...extras].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
        if (prev?.id === conversation.id && messageSignature(prev.messages) === messageSignature(merged)) {
          return prev;
        }
        return {
          ...base,
          client: conversation.client || base.client,
          channel: conversation.channel,
          lastMessage: conversation.lastMessage || base.lastMessage,
          clientId: conversation.clientId,
          twilioPhone: conversation.twilioPhone,
          messages: merged,
          unreadCount: 0,
          responseRequired: false
        };
      });

      setConversations(prev => {
        const current = prev.find((c) => c.id === conversation.id);
        if (!current || (current.unreadCount === 0 && !current.responseRequired)) return prev;
        return prev.map(c => c.id === conversation.id ? { ...c, unreadCount: 0, responseRequired: false } : c);
      });

      if (conversation.unreadCount > 0) {
        await twilioService.markAsRead(conversation.id);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      if (!background && sessionRef.current === session && loadTokenRef.current === token) {
        setSelectedConversation((prev) => (
          prev?.id === conversation.id ? prev : { ...conversation, messages: [] }
        ));
      }
    }
  };

  const loadMessagesRef = useRef(loadConversationMessages);
  loadMessagesRef.current = loadConversationMessages;

  const loadConversations = async (background = false) => {
    const session = sessionRef.current;
    if (!user?.id) return;
    if (!background) {
      setLoading(true);
      setError(null);
    }

    try {
      const { data, error: convError } = await twilioService.getConversations(user.id);
      if (sessionRef.current !== session) return;

      if (convError) {
        console.error('Error loading conversations:', convError);
        if (!background) setError('Failed to load conversations');
        return;
      }

      const formattedConversations = (data || []).map(formatConversation);
      setConversations((prev) => {
        const messagesById = new Map(prev.map((c) => [c.id, c.messages]));
        return formattedConversations.map((conv) => {
          const existingMessages = messagesById.get(conv.id);
          return existingMessages?.length ? { ...conv, messages: existingMessages } : conv;
        });
      });

      const currentId = selectedIdRef.current;
      let conversationToSelect = currentId
        ? formattedConversations.find((c) => c.id === currentId)
        : null;

      if (!conversationToSelect && !background) {
        const targetId = openedFromNavRef.current;
        conversationToSelect = (targetId && formattedConversations.find((c) => c.id === targetId))
          || formattedConversations[0]
          || null;
      }

      if (conversationToSelect) {
        await loadMessagesRef.current(conversationToSelect, {
          background: background || Boolean(currentId),
          session
        });
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
      if (sessionRef.current === session && !background) {
        setError('An error occurred while loading conversations');
      }
    } finally {
      if (sessionRef.current === session && !background) setLoading(false);
    }
  };

  const loadConversationsRef = useRef(loadConversations);
  loadConversationsRef.current = loadConversations;

  // Load real conversations from database. Later polls stay in the background
  // so the reply box is not unmounted while someone is typing.
  useEffect(() => {
    const session = ++sessionRef.current;
    if (!user?.id) return undefined;

    loadConversationsRef.current(false);
    const interval = setInterval(() => {
      if (sessionRef.current === session) loadConversationsRef.current(true);
    }, 10000);

    return () => {
      sessionRef.current += 1;
      clearInterval(interval);
    };
  }, [user?.id]);

  useEffect(() => {
    const targetId = location.state?.conversationId;
    if (!targetId || appliedNavIdRef.current === targetId) return;
    const match = conversations.find((c) => c.id === targetId);
    if (!match) return;
    appliedNavIdRef.current = targetId;
    openedFromNavRef.current = targetId;
    setIsMobileThreadView(true);
    loadMessagesRef.current(match);
  }, [location.state?.conversationId, conversations]);

  const handleSelectConversation = async (conversation) => {
    setIsMobileThreadView(true);
    
    // Set selected conversation immediately
    setSelectedConversation({
      ...conversation,
      messages: conversation.messages || []
    });
    
    // Load messages for this conversation
    await loadConversationMessages(conversation);

    // Mark as read
    setConversations((prev) =>
    prev?.map((c) =>
    c?.id === conversation?.id ?
    { ...c, unreadCount: 0, responseRequired: false } :
    c
    )
    );
  };

  const handleSendMessage = async (content) => {
    if (!selectedConversation || !content?.trim() || sending) return;

    setSending(true);
    
    // Optimistic UI update - show message immediately
    const tempMessage = {
      id: `temp-${Date.now()}`,
      content,
      timestamp: new Date(),
      isFromAgent: true,
      type: "agent",
      status: "sending"
    };

    setSelectedConversation((prev) => ({
      ...prev,
      messages: [...(prev?.messages || []), tempMessage],
      lastMessage: {
        content,
        timestamp: new Date(),
        isFromAgent: true,
        status: "sending"
      }
    }));

    setConversations((prev) =>
    prev?.map((c) =>
    c?.id === selectedConversation?.id ?
    {
      ...c,
          messages: [...(c?.messages || []), tempMessage],
      lastMessage: {
            content,
            timestamp: new Date(),
            isFromAgent: true,
            status: "sending"
      }
    } :
    c
    )
    );

    try {
      // Send SMS via Twilio using local area code matching
      const result = await twilioService.sendSMS({
        conversationId: selectedConversation.id,
        clientId: selectedConversation.clientId,
        agentId: user.id,
        toPhone: selectedConversation.client.phone,
        message: content
      });

      if (result.success) {
        console.log(`SMS sent using local number: ${result.data?.from_phone || 'default'}`);
        
        // Replace temp message with real message from database
        const realMessage = {
          id: result.data.id,
          content: result.data.content,
          timestamp: new Date(result.data.created_at),
          isFromAgent: true,
          type: "agent",
          status: result.data.status,
          twilioSid: result.twilioSid
        };

    setSelectedConversation((prev) => ({
      ...prev,
          messages: [...(prev?.messages?.filter(m => m.id !== tempMessage.id) || []), realMessage],
      lastMessage: {
            content: realMessage.content,
            timestamp: realMessage.timestamp,
            isFromAgent: true,
            status: realMessage.status
      }
    }));

      setConversations((prev) =>
      prev?.map((c) =>
      c?.id === selectedConversation?.id ?
      {
        ...c,
              messages: [...(c?.messages?.filter(m => m.id !== tempMessage.id) || []), realMessage],
              lastMessage: {
                content: realMessage.content,
                timestamp: realMessage.timestamp,
                isFromAgent: true,
                status: realMessage.status
              }
      } :
      c
      )
      );
      } else {
        // Failed to send - update message status
        setSelectedConversation((prev) => ({
          ...prev,
          messages: prev?.messages?.map(m =>
            m.id === tempMessage.id ? { ...m, status: "failed" } : m
          )
        }));
        
        setError('Failed to send message: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error sending message:', err);
      
      // Mark message as failed
      setSelectedConversation((prev) => ({
        ...prev,
        messages: prev?.messages?.map(m =>
          m.id === tempMessage.id ? { ...m, status: "failed" } : m
        )
      }));
      
      setError('An error occurred while sending the message');
    } finally {
      setSending(false);
    }
  };

  const handleBackToList = () => {
    setIsMobileThreadView(false);
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user?.id) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Conversations - Nurture Buddy.ai</title>
        <meta name="description" content="Manage all client conversations and communications in one centralized location" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 pb-20 md:pb-0">
          {/* Error Message */}
          {error && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={20} />
                  <span>{error}</span>
                </div>
                <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Stats Overview */}
          <ConversationStats conversations={conversations} />
          
          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading conversations...</p>
              </div>
            </div>
          ) : conversations.length === 0 ? (
            /* No Conversations State */
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center max-w-md px-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageSquare" size={32} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No conversations yet</h3>
                <p className="text-muted-foreground mb-6">
                  Start by creating a conversation with a client from the Client Management page.
                  Once you initiate a conversation, it will appear here.
                </p>
              </div>
            </div>
          ) : (
            /* Main Content */
          <div className="flex h-[calc(100vh-220px)] md:h-[calc(100vh-140px)]">
            {/* Mobile Back Button */}
              {isMobileThreadView && (
            <div className="md:hidden fixed top-20 left-4 z-10">
                <button
                onClick={handleBackToList}
                className="bg-card border border-border rounded-lg p-2 elevation-2">
                  <Icon name="ArrowLeft" size={20} />
                </button>
              </div>
              )}

            {/* Conversation List */}
            <div className={`w-full md:w-80 lg:w-96 flex-shrink-0 ${
                isMobileThreadView ? 'hidden md:block' : 'block'
              }`}>
              <ConversationList
                conversations={conversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
            </div>

            {/* Message Thread */}
            <div className={`flex-1 ${
                isMobileThreadView ? 'block' : 'hidden md:block'
              }`}>
                {selectedConversation ? (
              <MessageThread
                conversation={selectedConversation}
                    onSendMessage={handleSendMessage}
                    sending={sending}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-card border-l border-border">
                    <div className="text-center max-w-md px-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="MessageCircle" size={32} className="text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Select a conversation
                      </h3>
                      <p className="text-muted-foreground">
                        Choose a conversation from the list to start messaging
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Conversations;